import { CommonModule } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    Signal,
    ViewChild,
    ViewEncapsulation,
    computed,
    signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import {
    NavigationEnd,
    Router,
    RouterLink,
    RouterOutlet,
} from '@angular/router';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { LeafInputDirective } from 'app/@leaf/inputs/inputs/leaf-input.directive';
import { SidebarComponent } from 'app/components/sidebar/sidebar.component';
import { slideUpOnLeave } from 'app/core/animations/animations';
import { SmoothWidthDirective } from 'app/core/directives/smooth-width.directive';
import { NavbarComponent } from 'app/layout/components/navbar/navbar.component';
import { NavbarService } from 'app/layout/components/navbar/navbar.service';
import { NavigationService } from 'app/modules/navigation/navigation.service';
import { ProductionService } from 'app/modules/sho_management_pro/production/services/production.service';
import { Production } from 'app/modules/sho_management_pro/production/store/productions.models';
import { MediaWatcherService } from 'app/services/media-watcher.service';
import { UiService } from 'app/services/ui.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'sho-layout',
    standalone: true,
    imports: [
        CommonModule,
        RouterLink,
        LeafInputDirective,
        RouterOutlet,
        MatButtonModule,
        MatSidenavModule,
        SidebarComponent,
        FuseLoadingBarComponent,
        SmoothWidthDirective,
        NavbarComponent,
    ],
    templateUrl: './sho.component.html',
    encapsulation: ViewEncapsulation.None,
    animations: [slideUpOnLeave],
})
export class ShoLayoutComponent implements OnInit, OnDestroy {
    @ViewChild('matDrawer') matDrawer!: MatDrawer;
    drawerMode: 'over' | 'side' = 'side';
    drawerOpened: boolean = true;
    sidebarData = signal<FuseNavigationItem[]>([]);

    iconDrawerMode: 'over' | 'side' = 'side';
    iconDrawerOpened: boolean = false;
    iconSidebarData = signal<FuseNavigationItem[]>([]);

    isScreenSmall = signal<boolean>(false);
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    isInventoryRoute = signal<boolean>(false);
    currentProduction: Signal<Production | null> = computed(() =>
        this._productionService.currentProduction()
    );

    isIcon: boolean = false;
    actionIconNames = ['castle-02'];

    /**
     * Constructor
     */
    constructor(
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _cdRef: ChangeDetectorRef,
        private _router: Router,
        private _sidebarService: NavigationService,
        private _productionService: ProductionService,
        private _mediaWatcherService: MediaWatcherService,
        private _uiService: UiService,
        private _navbarService: NavbarService
    ) {}

    // ----------------------------------------------------------------------
    // @ Lifecycle hooks
    // ----------------------------------------------------------------------

    ngOnInit(): void {
        // Get sidebar items according the role
        const { lgMenuData, smMenuData } =
            this._sidebarService.getSideBarItems();

        this.sidebarData.set(lgMenuData);
        this.iconSidebarData.set(smMenuData);

        this.urlChanges();

        // Subscribe to navbar toggle
        this._navbarService.toggleDrawer$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.matDrawer.toggle();
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Set the drawerMode and drawerOpened if
                if (matchingAliases.includes('xl')) {
                    this.isScreenSmall.set(false);

                    this.iconDrawerMode = 'side';
                    this.iconDrawerOpened = false;

                    setTimeout(() => {
                        this.drawerMode = 'side';
                        this.drawerOpened = true;

                        this._cdRef.detectChanges();
                    }, 500);
                } else {
                    this.isScreenSmall.set(true);

                    this.drawerMode = 'side';
                    this.drawerOpened = false;
                    this.iconDrawerOpened = false;

                    setTimeout(() => {
                        this.iconDrawerMode = 'side';
                        this.iconDrawerOpened = true;

                        this._cdRef.detectChanges();
                    }, 500);
                }
            });

        // Registering Icons
        this._uiService.registerSvgIcons(this.actionIconNames);
        this.isIcon = true;
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    /**
     * @description
     * Hide the details cards on scroll
     */
    onScroll(event: any): void {
        const container = event.target as HTMLElement;
        const scrollTop = container.scrollTop;

        const lastValue = this._mediaWatcherService.scrollPosition();

        if (Math.abs(scrollTop - lastValue) > 20) {
            // cambia solo si hay diferencia de 20px
            this._mediaWatcherService.scrollPosition.set(scrollTop);
        }
    }

    /**
     * @description
     * Subscribe to route url changes and get the url segments
     */
    urlChanges() {
        const setSignal = (urlSegments: string[]) => {
            this.isInventoryRoute.set(
                urlSegments.includes('app') && urlSegments.includes('inventory')
            );
        };

        // Initial case
        const urlSegments = this._router.routerState.snapshot.url.split('/');
        setSignal(urlSegments);

        // Subscription to route changes
        this._router.events
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((event) => {
                if (event instanceof NavigationEnd) {
                    const urlSegments = window.location.href.split('/');
                    setSignal(urlSegments);
                }
            });
    }
}
